/**
 * HeavyMach add-on components — Notifications bell, Voice recorder/player,
 * PreferredBadge, BlockButton, LanguageSwitcher, CommissionBreakdown
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { C, F, Btn, money, fmtDateTime } from './UI';

/* ── Notification chime ──────────────────────────────
 * Soft two-note bell via Web Audio (no asset needed).
 * Kept short + low volume so it never irritates.        */
let _audioCtx = null;
let _lastChime = 0;
export function playNotificationChime() {
  try {
    const now = Date.now();
    if (now - _lastChime < 2000) return; // throttle bursts
    _lastChime = now;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    _audioCtx = _audioCtx || new AC();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    const ctx = _audioCtx;
    const t0 = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.06; // gentle
    master.connect(ctx.destination);
    [[880, 0], [1174.66, 0.13]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const s = t0 + delay;
      g.gain.setValueAtTime(0, s);
      g.gain.linearRampToValueAtTime(1, s + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, s + 0.35);
      osc.connect(g);
      g.connect(master);
      osc.start(s);
      osc.stop(s + 0.4);
    });
  } catch { /* audio not critical */ }
}

/* ── Language Switcher ───────────────────────────── */
export function LanguageSwitcher({ compact = false }) {
  const { lang, setLang, t } = useI18n();
  return (
    <select
      value={lang}
      onChange={(e) => setLang(e.target.value)}
      title={t('lang.label')}
      style={{
        background: compact ? 'transparent' : C.card,
        color: compact ? '#94a3b8' : C.ink,
        border: `1px solid ${compact ? 'rgba(255,255,255,.12)' : C.border}`,
        borderRadius: 6, padding: '5px 8px', fontSize: 12, fontFamily: F,
        cursor: 'pointer', outline: 'none',
      }}
    >
      <option value="en">EN · English</option>
      <option value="hi">HI · हिन्दी</option>
    </select>
  );
}

/* ── Notification Bell ───────────────────────────── */
export function NotificationBell() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const prevUnread = useRef(null);

  const load = useCallback(() => {
    if (!user) return;
    API.get('/notifications').then(r => {
      setItems(r.data.notifications || []);
      const n = r.data.unread || 0;
      // Chime only when something NEW arrives (not on first load).
      if (prevUnread.current !== null && n > prevUnread.current) {
        playNotificationChime();
      }
      prevUnread.current = n;
      setUnread(n);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const markAll = () => API.post('/notifications/read-all').then(() => { setUnread(0); load(); });
  const onClick = (n) => {
    API.post(`/notifications/${n.id}/read`).then(load);
    setOpen(false);
    if (n.link) nav(n.link);
  };

  if (!user) return null;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(v => !v)} style={{
        background: 'transparent', border: 'none', cursor: 'pointer', padding: 6,
        position: 'relative', display: 'flex', alignItems: 'center',
      }} title={t('notif.title')}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 2.5a5 5 0 00-5 5v3.7l-1.5 2.3h13L15 11.2V7.5a5 5 0 00-5-5z" stroke={C.muted} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M8 16a2 2 0 004 0" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2, background: C.red, color: '#fff',
            fontSize: 10, fontWeight: 700, fontFamily: F, padding: '1px 5px',
            borderRadius: 99, lineHeight: 1.2, minWidth: 16, textAlign: 'center',
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 360,
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,.12)', zIndex: 1000, overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontFamily: F, fontSize: 13, color: C.ink }}>{t('notif.title')}</strong>
            {unread > 0 && <button onClick={markAll} style={{ background: 'none', border: 'none', color: C.blue, fontSize: 12, fontFamily: F, cursor: 'pointer' }}>{t('notif.mark_all_read')}</button>}
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {items.length === 0
              ? <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: C.faint, fontFamily: F }}>{t('notif.empty')}</div>
              : items.slice(0, 30).map(n => (
                <div key={n.id} onClick={() => onClick(n)} style={{
                  padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
                  cursor: 'pointer', background: n.is_read == 0 ? C.surface : 'transparent',
                  fontFamily: F,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: C.ink }}>
                    {n.priority === 'urgent' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.red }} />}
                    {n.priority === 'high' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.orange }} />}
                    {n.title}
                  </div>
                  {n.body && <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.4 }}>{n.body}</div>}
                  <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>{fmtDateTime(n.created_at)}</div>
                </div>
              ))
            }
          </div>
          <div style={{ padding: 10, borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
            <button onClick={() => { setOpen(false); nav(`/${user.role}/notifications`); }} style={{ background: 'none', border: 'none', color: C.blue, fontSize: 12, fontFamily: F, cursor: 'pointer' }}>
              View all →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Voice Recorder ──────────────────────────────── */
export function VoiceRecorder({ contextType, contextId, onUploaded }) {
  const { t } = useI18n();
  const [rec, setRec] = useState(null);
  const [recording, setRecording] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [secs, setSecs] = useState(0);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const local = [];
      mr.ondataavailable = (e) => { if (e.data.size) local.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(local, { type: 'audio/webm' });
        setChunks(local);
        setPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(tr => tr.stop());
      };
      mr.start();
      setRec(mr);
      setRecording(true);
      setSecs(0);
      timerRef.current = setInterval(() => setSecs(s => s + 1), 1000);
    } catch (e) {
      toast.error('Microphone permission denied');
    }
  };

  const stop = () => {
    rec?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const upload = async () => {
    if (!chunks.length) return;
    setUploading(true);
    const fd = new FormData();
    const blob = new Blob(chunks, { type: 'audio/webm' });
    fd.append('audio', blob, `voice_${Date.now()}.webm`);
    fd.append('context_type', contextType || 'other');
    if (contextId) fd.append('context_id', contextId);
    fd.append('duration_seconds', secs);
    try {
      const r = await API.post('/voice-notes', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Voice note uploaded');
      setPreviewUrl(null);
      setChunks([]);
      setSecs(0);
      onUploaded?.(r.data);
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: F }}>
      {!recording && !previewUrl && (
        <button onClick={start} style={{ background: C.red, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
          {t('voice.record')}
        </button>
      )}
      {recording && (
        <>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: C.red, animation: 'pulse 1s infinite' }} />
          <span style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{t('voice.recording')} {fmt(secs)}</span>
          <button onClick={stop} style={{ background: C.ink, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            ■ {t('voice.stop')}
          </button>
          <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
        </>
      )}
      {previewUrl && !recording && (
        <>
          <audio src={previewUrl} controls style={{ height: 30, flex: 1 }} />
          <span style={{ fontSize: 12, color: C.muted }}>{fmt(secs)}</span>
          <button onClick={upload} disabled={uploading} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {uploading ? '...' : t('voice.upload')}
          </button>
          <button onClick={() => { setPreviewUrl(null); setChunks([]); setSecs(0); }} style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>×</button>
        </>
      )}
    </div>
  );
}

export function VoiceNotesList({ contextType, contextId, refreshKey }) {
  const { t } = useI18n();
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    if (!contextId) return;
    API.get(`/voice-notes/${contextType}/${contextId}`).then(r => setNotes(r.data.notes || [])).catch(() => {});
  }, [contextType, contextId, refreshKey]);
  if (!notes.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, fontFamily: F, marginBottom: 6 }}>🎙 {t('voice.attached')} ({notes.length})</div>
      {notes.map(n => (
        <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: C.bg, borderRadius: 6, marginBottom: 5 }}>
          <span style={{ fontSize: 11, fontFamily: F, color: C.muted, minWidth: 90 }}>{n.user_name}</span>
          <audio src={n.file_url} controls style={{ height: 28, flex: 1 }} />
          <span style={{ fontSize: 11, color: C.faint, fontFamily: F }}>{fmtDateTime(n.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Preferred Badge ─────────────────────────────── */
export function PreferredBadge({ size = 'sm' }) {
  const { t } = useI18n();
  const pad = size === 'sm' ? '2px 8px' : '4px 10px';
  const fs = size === 'sm' ? 10 : 11;
  return (
    <span title={t('vendor.preferred_badge')} style={{
      background: 'linear-gradient(135deg,#fef3c7,#fde68a)',
      color: '#92400e', padding: pad, borderRadius: 99,
      fontSize: fs, fontWeight: 700, fontFamily: F,
      display: 'inline-flex', alignItems: 'center', gap: 4,
      border: '1px solid #fcd34d', whiteSpace: 'nowrap',
    }}>
      ★ {t('vendor.preferred_badge')}
    </span>
  );
}

/* ── Preferred toggle button ─────────────────────── */
export function PreferredToggle({ supplierId, onChange }) {
  const { t } = useI18n();
  const [pref, setPref] = useState(null);
  useEffect(() => {
    API.get(`/vendors/preferred/${supplierId}/check`).then(r => setPref(!!r.data.preferred)).catch(() => setPref(false));
  }, [supplierId]);
  const toggle = () => {
    API.post(`/vendors/preferred/${supplierId}`).then(r => {
      setPref(r.data.preferred);
      onChange?.(r.data.preferred);
      toast.success(r.data.message);
    });
  };
  if (pref === null) return null;
  return (
    <button onClick={toggle} style={{
      background: pref ? '#fef3c7' : 'transparent',
      color: pref ? '#92400e' : C.muted,
      border: `1px solid ${pref ? '#fcd34d' : C.border}`,
      borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600,
      fontFamily: F, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>
      {pref ? '★' : '☆'} {pref ? t('common.remove_preferred') : t('common.mark_preferred')}
    </button>
  );
}

/* ── Block / Unblock button ──────────────────────── */
export function BlockButton({ userId, onBlocked }) {
  const { t } = useI18n();
  const block = () => {
    const reason = prompt(t('vendor.block_reason') || 'Reason (optional):');
    if (reason === null) return;
    API.post('/vendors/block', { user_id: userId, reason }).then(() => {
      toast.success('Blocked');
      onBlocked?.();
    });
  };
  return (
    <button onClick={block} style={{
      background: 'transparent', color: C.red, border: `1px solid ${C.red}`,
      borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600,
      fontFamily: F, cursor: 'pointer',
    }}>
      🚫 {t('common.block')}
    </button>
  );
}

/* ── Commission Breakdown card (WO context) ──────── */
export function CommissionBreakdown({ workOrderId }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!workOrderId) return;
    API.get(`/commission/work-order/${workOrderId}`).then(r => setData(r.data)).catch(() => {});
  }, [workOrderId]);
  if (!data) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, fontFamily: F }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 10 }}>
        💼 {t('commission.title')} ({data.commission_rate}%)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted }}>{t('commission.project_value')}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{money(data.project_value)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.muted }}>{t('commission.deducted')}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.red }}>-{money(data.commission_amount)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.muted }}>{t('commission.net_payout')}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.green }}>{money(data.supplier_payout)}</div>
        </div>
      </div>
      {(data.records || []).length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: C.muted }}>
          {data.records.length} record(s) · Collected: {money(data.commission_collected)} · Paid out: {money(data.supplier_paid_out)}
        </div>
      )}
    </div>
  );
}
